/* eslint-disable react/prop-types */
/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2023. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import { Authentication } from '@spm/universal-access';
import { ErrorBoundary } from '@spm/core-ui';
import { connect } from 'react-redux';
import OrganisationComponent from './components/OrganisationComponent';
import {
  ACESWaitlistSelectors,
  ACESWaitlistActions,
  ACESFIActions,
  ACESFISelectors,
} from '../../modules/generated';

/**
 * OrganisationContainer initiates the rendering of the Organisation
 * page.
 *
 * @class OrganisationContainer
 * @extends {Component}
 */
class OrganisationContainer extends Component {
  /**
   * Invoked just before mounting occurs.
   *
   * @memberof OrganisationContainer
   * @see https://reactjs.org/docs/react-component.html#componentdidmount
   */
  componentDidMount() {
    // In case there is a Generated type user logged in, this citizen may potentially have
    // applied for a benefit during the active session (by hitting the Benefits tab without actually
    // logging in, a generated type user will be created/logged in so that the citizen can apply
    // for a benefit even being unauthenticated) providing his/her personal information.
    // For that reason, we need to make sure that the generated user will be logged out as soon as
    // he/she navigates back to Home Page in order to avoid the next citizen accessing a public
    // computer having access to the previous citizen's application data.
    const { isFundAvailable, userStatus } = this.props;

    isFundAvailable();
    userStatus();

    if (Authentication.userTypeIs([Authentication.USER_TYPES.GENERATED])) {
      Authentication.logout(() => {
        // WP4: User Registration: This API call manually logs in as public citizen upon user
        // sign out to avoid server error. This is required when
        // 1. User clicks on Apply for benefits --> Apply
        // 2. System takes the user to log-in page
        // 3. User clicks on forgot username or forgot pasword
        // 4. User clicks on home back button on top left (which call this 'OrganisationContainer' logout())
        // 5. User clicks on Apply for benefits (navigate to BenefitComponent)
        // And required to relogin to do rest call for 'emailVerifiedInd' check on BenefitContainer.
        Authentication.loginAsPublicCitizen(this.loginAsPublicCitizenCallback);
      });
    }
  }

  loginAsPublicCitizenCallback = () => {
    // console.log('Logging in as public citizen')
  };

  /**
   * Renders the Organisation page using {@link OrganisationComponent} or an
   * {@link Alert} if there was a problem loading the data.
   * @returns {@link OrganisationComponent} or {@link Alert}
   * @memberof OrganisationContainer
   */
  render() {
    return (
      <ErrorBoundary>
        <OrganisationComponent {...this.props} />
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  isFundAvailableInd: ACESWaitlistSelectors.fetchAcesChapWaitlistFundAvailable(state),
  userStatusInd: ACESFISelectors.fetchAcesFiChapApplied(state),
  isFundRetrievingInd: ACESFISelectors.isFetchingFetchAcesFiChapApplied(state),
});

export const mapDispatchToProps = dispatch => ({
  isFundAvailable: () => ACESWaitlistActions.fetchAcesChapWaitlistFundAvailable(dispatch),
  userStatus: () => ACESFIActions.fetchAcesFiChapApplied(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganisationContainer);
